import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
// Imported by file rather than through the barrel: a guard in core/auth reads this service, and
// going through both barrels would make the two folders import each other.
import { AuthStateService } from 'app/core/auth/auth-state-service';

/** The area of the application a URL belongs to. */
export type Workspace = 'admin' | 'lab-admin' | 'doctor' | 'technician';

const Workspaces: readonly Workspace[] = ['admin', 'lab-admin', 'doctor', 'technician'];

/**
 * Which part of the application the current URL sits in, read off the route rather than off the
 * signed-in roles.
 *
 * One person can hold several roles at once — an administrator is usually also a doctor — so
 * "who is this" cannot say which of two order lists they are looking at. The prefix they
 * navigated through can, and it survives a refresh, a deep link and the back button.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceContext {
  private readonly _router = inject(Router);
  private readonly _authState = inject(AuthStateService);

  /** The workspace of the page being shown. */
  get current(): Workspace {
    return this._declared() ?? this.owned;
  }

  /**
   * The workspace this person belongs to, whatever page they are looking at.
   *
   * Each branch names the role it belongs to rather than falling through, so a role nobody
   * has taught this service about lands in the doctor workspace it can at least read, instead
   * of the lab workspace where every page is scoped to a lab it does not have. Admin is asked
   * before Lab Admin, so an account holding both keeps the wider workspace.
   */
  get owned(): Workspace {
    if (this._authState.hasAdminRole()) {
      return 'admin';
    }
    if (this._authState.hasLabAdminRole()) {
      return 'lab-admin';
    }
    return this._authState.hasLabTechnicianRole() ? 'technician' : 'doctor';
  }

  /**
   * Where a workspace starts. Derived from the same value as `owned` on purpose: if the landing
   * page of a workspace did not itself belong to that workspace, sending someone there for not
   * belonging would send them somewhere they also do not belong, and the redirect would loop.
   */
  homeUrlOf(workspace: Workspace): string {
    switch (workspace) {
      case 'admin':
        return '/m/admin';
      case 'lab-admin':
        return '/m/lab-admin';
      case 'technician':
        return '/m/technician';
      default:
        return '/m/orders';
    }
  }

  /** The workspace a route declares, if any, taken from the deepest declaration above it. */
  declaredOn(route: ActivatedRouteSnapshot): Workspace | null {
    for (let current: ActivatedRouteSnapshot | null = route; current !== null; current = current.parent) {
      const value: unknown = current.data['workspace'];
      if (Workspaces.includes(value as Workspace)) {
        return value as Workspace;
      }
    }
    return null;
  }

  get isAdmin(): boolean {
    return this.current === 'admin';
  }

  get isLabAdmin(): boolean {
    return this.current === 'lab-admin';
  }

  get isDoctor(): boolean {
    return this.current === 'doctor';
  }

  get isTechnician(): boolean {
    return this.current === 'technician';
  }

  /**
   * The deepest `workspace` declared on the active route chain, so a page keeps the answer of
   * the prefix it was mounted under however its own children are configured. Walking the chain
   * rather than reading one route's data also avoids turning on route data inheritance globally.
   */
  private _declared(): Workspace | null {
    let route: ActivatedRouteSnapshot | null = this._router.routerState.snapshot.root;
    let declared: Workspace | null = null;

    while (route !== null) {
      const value: unknown = route.data['workspace'];
      if (Workspaces.includes(value as Workspace)) {
        declared = value as Workspace;
      }
      route = route.firstChild;
    }

    return declared;
  }
}
